import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  LayoutList,
  History,
  Lightbulb,
  Bot,
} from "lucide-react";
import { AiraPanel } from "@/components/AiraPanel";
import "katex/dist/katex.min.css";
import { MarkdownRenderer } from "./ui/markdown-renderer";
import { useAuth } from "@/contexts/AuthContext";
import { hasAiraAccess } from "@/lib/subscriptions";
import { loadQuestionsWithSource, type Question } from "@/lib/questionLoader";
import {
  getSessionsForTopic,
  getAttemptsForSession,
  getAllTopicAttempts,
  type SessionInfo,
  type QuestionAttempt,
} from "@/lib/progress";

interface ReviewQuestion {
  question: Question;
  userAnswer: string | null;
  isCorrect: boolean;
}

type ReviewView = "mode-select" | "session-list" | "reviewing";


function ModeSelectView({
  topicId,
  onSelectAll,
  onSelectSessions,
}: {
  topicId: string;
  onSelectAll: () => void;
  onSelectSessions: () => void;
}) {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="h-14 border-b border-border flex items-center px-6 gap-4">
        <Button variant="ghost" size="icon" onClick={() => window.history.back()}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-semibold text-lg capitalize">{topicId.replace(/_/g, " ")}</h1>
          <p className="text-xs text-muted-foreground">Choose a mode</p>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-3xl">
          <h2 className="text-2xl font-bold mb-2 text-center">What would you like to do?</h2>
          <p className="text-muted-foreground text-center mb-10">
            Review what you've already attempted.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <button
              onClick={onSelectAll}
              className="group text-left p-6 rounded-2xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <LayoutList className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-1">All Attempted Questions</h3>
              <p className="text-sm text-muted-foreground">
                Every question you've answered for this topic, showing your most recent attempt.
              </p>
            </button>

            <button
              onClick={onSelectSessions}
              className="group text-left p-6 rounded-2xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <History className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-1">By Session</h3>
              <p className="text-sm text-muted-foreground">
                Pick a specific practice session and review those exact questions and answers.
              </p>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

function SessionListView({
  sessions,
  onBack,
  onSelect,
}: {
  sessions: SessionInfo[];
  onBack: () => void;
  onSelect: (s: SessionInfo) => void;
}) {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="h-14 border-b border-border flex items-center px-6 gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="font-semibold text-lg">Past Sessions</h1>
      </header>

      <main className="flex-1 max-w-2xl w-full mx-auto px-6 py-8">
        {sessions.length === 0 ? (
          <div className="text-center text-muted-foreground py-20">
            No sessions found for this topic yet.
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map((s, i) => {
              const accuracy = s.questionCount > 0
                ? Math.round((s.correctCount / s.questionCount) * 100)
                : 0;
              const date = new Date(s.createdAt).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              });
              const time = new Date(s.createdAt).toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
              });

              return (
                <button
                  key={s.sessionId}
                  onClick={() => onSelect(s)}
                  className="w-full text-left p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-muted/40 transition-all flex items-center justify-between group"
                >
                  <div>
                    <p className="font-medium">
                      Session {sessions.length - i}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {date} at {time}
                    </p>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-right">
                      <p className="font-semibold">
                        {s.correctCount}/{s.questionCount}
                      </p>
                      <p className="text-muted-foreground text-xs">questions</p>
                    </div>
                    <div
                      className={cn(
                        "w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm border-2",
                        accuracy >= 80
                          ? "border-green-300 text-green-700 bg-green-50"
                          : accuracy >= 50
                          ? "border-yellow-300 text-yellow-700 bg-yellow-50"
                          : "border-red-300 text-red-700 bg-red-50"
                      )}
                    >
                      {accuracy}%
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function ReviewingView({
  reviewQuestions,
  title,
  topicId,
  userId,
  airaEnabled = false,
  onBack,
}: {
  reviewQuestions: ReviewQuestion[];
  title: string;
  topicId: string;
  userId?: string;
  airaEnabled?: boolean;
  onBack: () => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswers, setShowAnswers] = useState(true);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showAira, setShowAira] = useState(false);
  const [dividerPosition, setDividerPosition] = useState(50);
  const [isDraggingDivider, setIsDraggingDivider] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setShowExplanation(false);
  }, [currentIndex]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingDivider || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newPos = ((e.clientX - rect.left) / rect.width) * 100;
      setDividerPosition(Math.min(Math.max(newPos, 20), 80));
    };
    const handleMouseUp = () => setIsDraggingDivider(false);
    if (isDraggingDivider) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDraggingDivider]);

  if (reviewQuestions.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">No attempted questions found for this selection.</p>
        <Button onClick={onBack}>Go Back</Button>
      </div>
    );
  }

  const current = reviewQuestions[currentIndex]!;
  const { question, userAnswer, isCorrect } = current;
  const questionNumber = currentIndex + 1;
  const correctCount = reviewQuestions.filter((q) => q.isCorrect).length;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="h-14 border-b border-border flex items-center justify-between px-4 bg-background sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold">Review Mode</h1>
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                {title}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 text-sm">
            <span className="text-green-600 font-medium">{correctCount} correct</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-red-500 font-medium">{reviewQuestions.length - correctCount} incorrect</span>
            <span className="text-muted-foreground">·</span>
            <span className="font-medium">{reviewQuestions.length} total</span>
          </div>
          <button
            onClick={() => { setShowAnswers((v) => !v); setShowExplanation(false); }}
            className={cn(
              "flex items-center gap-2 text-sm px-3 py-1.5 rounded-full border transition-all font-medium",
              showAnswers
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:border-primary/50"
            )}
          >
            {showAnswers ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
            {showAnswers ? "Answers shown" : "Answers hidden"}
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className="w-56 border-r border-border bg-muted/30 p-4 flex flex-col gap-4 overflow-auto shrink-0">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Questions</p>
            <div className="grid grid-cols-4 gap-1.5">
              {reviewQuestions.map((rq, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={cn(
                    "h-9 rounded-lg text-xs font-medium transition-all border",
                    idx === currentIndex && "ring-2 ring-primary",
                    showAnswers
                      ? rq.isCorrect
                        ? "bg-green-100 text-green-800 border-green-200"
                        : "bg-red-100 text-red-800 border-red-200"
                      : "bg-background border-border"
                  )}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-background p-3 space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Correct</span>
              <span className="font-medium text-green-600">{correctCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Incorrect</span>
              <span className="font-medium text-red-500">{reviewQuestions.length - correctCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Accuracy</span>
              <span className="font-medium">
                {Math.round((correctCount / reviewQuestions.length) * 100)}%
              </span>
            </div>
          </div>
        </aside>

        {/* Main area */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Split panel */}
          <div
            ref={containerRef}
            className="flex-1 flex relative overflow-hidden min-h-0"
            style={{ cursor: isDraggingDivider ? "col-resize" : undefined }}
          >
            {/* Left: passage */}
            <div
              className="h-full overflow-auto border-r border-border"
              style={{ width: `${dividerPosition}%` }}
            >
              <div className="p-8 max-w-prose">
                {question.passageImage && (
                  <img src={question.passageImage} alt="" className="w-full rounded-lg mb-4" />
                )}
                {question.passage && (
                  <div className="text-lg leading-relaxed prose prose-neutral">
                    <MarkdownRenderer content={question.passage} />
                  </div>
                )}
                {!question.passage && !question.passageImage && (
                  <p className="text-muted-foreground italic">No passage for this question.</p>
                )}
              </div>
            </div>

            {/* Divider */}
            <div
              className="w-1.5 bg-border hover:bg-primary/20 cursor-col-resize flex items-center justify-center transition-colors"
              onMouseDown={() => setIsDraggingDivider(true)}
            >
              <div className="w-1 h-8 rounded-full bg-muted-foreground/30" />
            </div>

            {/* Right: question + options */}
            <div
              className="h-full overflow-auto"
              style={{ width: `${100 - dividerPosition}%` }}
            >
              <div className="p-6">
                {/* Status badge */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-foreground text-background rounded-lg flex items-center justify-center font-semibold text-sm">
                    {questionNumber}
                  </div>
                  {showAnswers && (
                    <span
                      className={cn(
                        "flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full",
                        isCorrect ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      )}
                    >
                      {isCorrect ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                      {isCorrect ? "Correct" : "Incorrect"}
                    </span>
                  )}
                </div>

                {/* Question text */}
                <div className="mb-6">
                  {question.questionImage && (
                    <img src={question.questionImage} alt="" className="w-full max-w-md rounded-lg mb-4" />
                  )}
                  <div className="text-lg">
                    <MarkdownRenderer content={question.question} />
                  </div>
                </div>

                {/* Options */}
                <div className="space-y-3 mb-6">
                  {question.options.map((option) => {
                    const wasSelected = userAnswer === option.id;
                    const isCorrectOption = option.isCorrect;

                    return (
                      <div
                        key={option.id}
                        className={cn(
                          "flex items-center gap-4 p-4 rounded-xl border-2 transition-colors",
                          showAnswers
                            ? isCorrectOption
                              ? "border-green-300 bg-green-50 text-green-900"
                              : wasSelected
                              ? "border-red-300 bg-red-50 text-red-900"
                              : "border-border text-foreground opacity-60"
                            : wasSelected
                            ? "border-primary bg-primary/5 text-foreground"
                            : "border-border text-foreground"
                        )}
                      >
                        <div
                          className={cn(
                            "w-8 h-8 rounded-full border-2 flex items-center justify-center font-semibold text-sm shrink-0",
                            showAnswers
                              ? isCorrectOption
                                ? "border-green-400 bg-green-100 text-green-900"
                                : wasSelected
                                ? "border-red-400 bg-red-100 text-red-900"
                                : "border-muted-foreground/30"
                              : wasSelected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-foreground"
                          )}
                        >
                          {option.id}
                        </div>
                        <div className="flex-1">
                          <MarkdownRenderer content={option.text} />
                        </div>
                        {showAnswers && isCorrectOption && (
                          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                        )}
                        {showAnswers && wasSelected && !isCorrectOption && (
                          <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Explanation */}
                {showAnswers && showExplanation && (
                  <div className="bg-muted/50 rounded-xl p-4 border border-border">
                    <h4 className="font-medium mb-2 flex items-center gap-2 text-sm">
                      <Lightbulb className="h-4 w-4 text-yellow-500" />
                      Explanation
                    </h4>
                    <MarkdownRenderer content={question.explanation} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="h-16 border-t border-border flex items-center justify-between px-4 bg-background sticky bottom-0 z-30">
            <span className="text-sm text-muted-foreground font-medium">
              {questionNumber} of {reviewQuestions.length}
            </span>

            <div className="flex items-center gap-3">
              {showAnswers && (
                <Button
                  variant="ghost"
                  className="gap-2"
                  onClick={() => setShowExplanation((v) => !v)}
                >
                  <Lightbulb className="h-4 w-4" />
                  {showExplanation ? "Hide" : "Show"} Explanation
                </Button>
              )}

              {airaEnabled ? (
                <Button
                  variant={showAira ? "default" : "ghost"}
                  className={cn(
                    "gap-2",
                    !showAira && "text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                  )}
                  onClick={() => setShowAira((v) => !v)}
                >
                  <Bot className="h-4 w-4" />
                  Ask Aira
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  className="gap-2 text-muted-foreground"
                  onClick={() => (window.location.href = "/paywall")}
                  title="Upgrade to Pro for Aira AI tutoring"
                >
                  <Bot className="h-4 w-4" />
                  Ask Aira ✦
                </Button>
              )}

              <div className="flex items-center border-l border-border pl-3 gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCurrentIndex((i) => Math.min(reviewQuestions.length - 1, i + 1))}
                  disabled={currentIndex === reviewQuestions.length - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          </footer>

          {/* Progress bar */}
          <div className="w-full h-1 bg-border/20">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${(questionNumber / reviewQuestions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {airaEnabled && <AiraPanel
        isOpen={showAira}
        onClose={() => setShowAira(false)}
        topic={topicId}
        question={question}
        selectedAnswer={userAnswer}
        isChecked={showAnswers}
      />}
    </div>
  );
}

export function ReviewPage() {
  const { user, loading: authLoading, subscriptions } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const subjectId = params.get("subject") || "";
  const moduleId = params.get("module") || "";
  const topicId = params.get("topic") || "general";

  const [view, setView] = useState<ReviewView>("mode-select");
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [reviewQuestions, setReviewQuestions] = useState<ReviewQuestion[]>([]);
  const [reviewTitle, setReviewTitle] = useState("");
  const [loading, setLoading] = useState(false);

  const buildReviewQuestions = async (attempts: QuestionAttempt[]): Promise<ReviewQuestion[]> => {
    const { questions: allQuestions } = await loadQuestionsWithSource(subjectId, moduleId, {
      topicId: topicId !== "general" ? topicId : undefined,
    });
    const questionMap = new Map(allQuestions.map((q) => [String(q.id), q]));

    return attempts
      .map((a) => {
        const q = questionMap.get(String(a.questionId));
        if (!q) return null;
        return { question: q, userAnswer: a.selectedAnswer, isCorrect: a.isCorrect };
      })
      .filter(Boolean) as ReviewQuestion[];
  };

  const handleSelectAll = async () => {
    if (!user) return;
    setLoading(true);
    const attempts = await getAllTopicAttempts(user.$id, topicId);
    const rqs = await buildReviewQuestions(attempts);
    setReviewQuestions(rqs);
    setReviewTitle("All Attempted Questions");
    setLoading(false);
    setView("reviewing");
  };

  const handleSelectSessions = async () => {
    if (!user) return;
    setLoading(true);
    const s = await getSessionsForTopic(user.$id, topicId);
    setSessions(s);
    setLoading(false);
    setView("session-list");
  };

  const handleSelectSession = async (session: SessionInfo) => {
    if (!user) return;
    setLoading(true);
    const attempts = await getAttemptsForSession(user.$id, topicId, session.sessionId);
    const rqs = await buildReviewQuestions(attempts);
    const date = new Date(session.createdAt).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    setReviewQuestions(rqs);
    setReviewTitle(`Session — ${date}`);
    setLoading(false);
    setView("reviewing");
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Please log in to access review mode.</p>
        <Button onClick={() => (window.location.href = "/login")}>Log In</Button>
      </div>
    );
  }

  if (view === "mode-select") {
    return (
      <ModeSelectView
        topicId={topicId}
        onSelectAll={handleSelectAll}
        onSelectSessions={handleSelectSessions}
      />
    );
  }

  if (view === "session-list") {
    return (
      <SessionListView
        sessions={sessions}
        onBack={() => setView("mode-select")}
        onSelect={handleSelectSession}
      />
    );
  }

  return (
    <ReviewingView
      reviewQuestions={reviewQuestions}
      title={reviewTitle}
      topicId={topicId}
      userId={user.$id}
      airaEnabled={hasAiraAccess(subscriptions)}
      onBack={() => setView(sessions.length > 0 ? "session-list" : "mode-select")}
    />
  );
}

export default ReviewPage;
